import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND, BRAND_SLIDES } from '@/config/brand';
import { cn } from '@/lib/cn';

/**
 * Accroche défilante de l'accueil.
 *
 * Trois messages — louer, acheter, vendre — se succèdent de haut en bas.
 * L'accueil ne parlait que de location : un visiteur venu pour acheter n'avait
 * aucune raison de deviner le reste.
 *
 * Trois choix méritent d'être expliqués :
 *
 * · Les trois accroches sont TOUJOURS dans le DOM, empilées dans une même
 *   cellule de grille. La hauteur du bloc vaut donc celle de la plus haute, à
 *   n'importe quelle largeur d'écran, et rien ne saute au changement. Réserver
 *   une hauteur fixe aurait marché sur un écran et cassé sur les autres.
 *
 * · Le défilement s'arrête au survol et au clavier. Une accroche qui change
 *   pendant qu'on lit, ou juste avant qu'on clique, est une nuisance.
 *
 * · `prefers-reduced-motion` supprime le mouvement, pas le contenu : les
 *   messages continuent d'alterner, en fondu, sans glissement.
 */

/**
 * Durée d'affichage de chaque accroche, en millisecondes.
 *
 * Trois secondes : le temps de lire la phrase et son sous-titre, sans que le
 * bloc paraisse figé. Le cycle complet des trois offres dure neuf secondes,
 * donc un visiteur qui arrive les voit toutes avant d'avoir rempli sa
 * recherche.
 *
 * Une seule valeur à changer pour ajuster. En dessous de 2000, le message n'a
 * plus le temps d'être lu ; au-delà de 5000, la troisième accroche passe
 * souvent inaperçue.
 */
const INTERVAL = 3000;

/** La transition doit rester bien plus courte que INTERVAL, sinon une accroche
 *  est encore en train d'entrer quand la suivante la pousse. */
const TRANSITION_MS = 300;

export function HeroHeadline() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % BRAND_SLIDES.length),
      INTERVAL,
    );
    return () => window.clearInterval(timer);
  }, [paused]);

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <p className="text-xs font-bold uppercase tracking-[0.08em] text-brand-600">{BRAND.market}</p>

      {/* Toutes les accroches occupent la même cellule : la hauteur du bloc est
          celle de la plus longue, et le passage de l'une à l'autre ne déplace
          jamais ce qui suit. */}
      <div className="mt-3.5 grid">
        {BRAND_SLIDES.map((slide, position) => {
          const active = position === index;
          return (
            <div
              key={slide.key}
              aria-hidden={!active}
              style={{ transitionDuration: `${TRANSITION_MS}ms` }}
              className={cn(
                '[grid-area:1/1] transition-all ease-out',
                active ? 'opacity-100' : 'pointer-events-none opacity-0',
                !reduced && !active && 'translate-y-4',
                !reduced && active && 'translate-y-0',
              )}
            >
              <h1 className="max-w-[15ch] text-[clamp(32px,5.5vw,56px)] font-extrabold leading-[1.08] tracking-tight text-ink-950">
                {slide.title} <span className="text-brand-500">{slide.accent}</span>
              </h1>
              <p className="mt-4 max-w-[520px] text-[clamp(15px,1.6vw,18px)] leading-relaxed text-ink-700">
                {slide.text}
              </p>
              <Link
                to={slide.cta.to}
                tabIndex={active ? 0 : -1}
                className="mt-4 inline-flex items-center gap-1.5 text-[15px] font-bold text-brand-600 hover:text-brand-700"
              >
                {slide.cta.label}
                <span aria-hidden>→</span>
              </Link>
            </div>
          );
        })}
      </div>

      {/* Trois repères, cliquables : celui qui veut voir « vendre » tout de
          suite n'a pas à attendre dix secondes. */}
      <div className="mt-5 flex items-center gap-2">
        {BRAND_SLIDES.map((slide, position) => (
          <button
            key={slide.key}
            type="button"
            onClick={() => setIndex(position)}
            aria-label={`Voir « ${slide.title} ${slide.accent} »`}
            aria-current={position === index}
            className={cn(
              'h-1.5 rounded-full transition-all',
              position === index ? 'w-7 bg-brand-500' : 'w-3 bg-ink-300 hover:bg-ink-400',
            )}
          />
        ))}
      </div>
    </div>
  );
}

/** Respecte le réglage système « réduire les animations ». */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  const query = useRef<MediaQueryList | null>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    query.current = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.current.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.current.addEventListener('change', onChange);
    return () => query.current?.removeEventListener('change', onChange);
  }, []);

  return reduced;
}
